import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  ActivityCategoriesIdsMap,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  GeoLocation,
  OrganizationalUnit,
  Shift,
  TimelineShift,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import {
  dateToPositionPx,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  generateDummyShift,
  getDateFromPixelPosition,
  HorizontalDemarcatorRange,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineState,
  TimelineAssignedShiftValidationsStateService,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertActivityV2,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { addHours, startOfMinute } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Injectable()
export class NewShiftDropZoneHelperService {
  private timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private dragAndDropModeService = inject(DragAndDropModeService);
  private shiftsService = inject(ShiftsService);
  private store = inject(Store);
  private timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private nzMessageService = inject(NzMessageService);
  private translateService = inject(TranslateService);
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );
  private readonly injector = inject(Injector);
  generateDateFromOffsetX(offsetX: number): Date {
    return getDateFromPixelPosition({
      pixelPosition: offsetX,
      timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
      frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
    });
  }

  handleOnDragOverProcess({
    shiftStartDate,
    offsetX,
    organizationalUnit,
    userId,
    location,
  }: {
    shiftStartDate: Date;
    organizationalUnit: OrganizationalUnit | undefined | null;
    location: GeoLocation | null;
    offsetX: number;
    userId: string | undefined | null;
  }): {
    mockShift: TimelineShiftWithActivities;
    mockShiftWithoutActivities: TimelineShift;
    currentDemarcatorRange: HorizontalDemarcatorRange;
  } | null {
    const shiftEndDate = startOfMinute(addHours(shiftStartDate, 8));

    const currentDemarcatorRange = {
      left: offsetX,
      right: dateToPositionPx(shiftEndDate, {
        fromRightSide: true,
        timelineWidthPixel: this.store.selectSnapshot(
          ShiftTimelineState.frameLength,
        ),
        interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
      }),
      shiftDuration: null,
      adjustmentNeeded: true,
    };

    if (organizationalUnit && location && userId) {
      const activityCategoryId = ActivityCategoriesIdsMap.More;
      const mockShift = generateDummyShift({
        orgUnit: organizationalUnit,
        location: location,
        shiftEndDate: shiftEndDate,
        shiftStartDate: shiftStartDate,
        userId: userId,
        activityCategoryId,
        injector: this.injector,
      });

      const mockShiftWithoutActivities =
        this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
          mockShift,
        ) as TimelineShift;

      this.store.dispatch(
        new TimelineUpsertNewShiftV2({
          shift: mockShiftWithoutActivities,
        }),
      );

      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
      this.dragAndDropModeService.setHorizontalDemarcatorRange(
        currentDemarcatorRange,
      );

      return {
        mockShift,
        mockShiftWithoutActivities,
        currentDemarcatorRange,
      };
    } else {
      return null;
    }
  }

  handleOnDropProcess({
    currentDemarcatorRange,
    mockShift,
    mockShiftWithoutActivities,
    userId,
  }: {
    currentDemarcatorRange: HorizontalDemarcatorRange | null;
    mockShift: TimelineShiftWithActivities;
    mockShiftWithoutActivities: TimelineShift;
    userId: string | undefined;
  }): void {
    if (mockShift && mockShiftWithoutActivities && userId) {
      this.createNewViaDragAndDropService.addUpdatingId(
        mockShiftWithoutActivities.id,
      );
      this.createNewViaDragAndDropService.setQuickShiftDraggable(false);
      this.dragAndDropModeService.setHorizontalDemarcatorRange({
        left: null,
        right: null,
        shiftDuration: null,
        adjustmentNeeded: true,
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { projectId, ...shiftWithoutProjectId } = mockShift;

      this.shiftsService.createShifts([shiftWithoutProjectId]).subscribe({
        next: async (materializedShifts: Shift[]) => {
          const shifts = await firstValueFrom(
            this.shiftsService.getResolvedShift(
              materializedShifts[0].id as string,
            ),
          );
          this.store.dispatch([
            new TimelineRemoveShiftFromUserV2({
              userId: userId,
              shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            }),
          ]);
          this.store.dispatch([
            new TimelineUpsertNewShiftV2({
              shift: {
                ...mockShiftWithoutActivities,
                id: shifts[0].id as string,
                activities: shifts[0].activities,
              },
            }),
          ]);
          this.store.dispatch(
            new TimelineUpsertActivityV2(
              shifts[0]
                .activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
              shifts[0].id as string,
            ),
          );

          this.createNewViaDragAndDropService.setQuickShiftDraggable(true);

          this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
            [mockShift.userId as string],
          );

          this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
            [mockShift.userId as string],
          );
          this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
          this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
            [shifts[0].id as string],
            {
              forceRefresh: true,
            },
          );
          this.shiftMultiSelectHelperService.setSelectedShift([
            {
              id: materializedShifts[0].id as string,
              left: currentDemarcatorRange?.left || null,
              right: currentDemarcatorRange?.right || null,
              isUnAssignedShift: false,
            },
          ]);
        },
        error: () => {
          this.store.dispatch(
            new TimelineRemoveShiftFromUserV2({
              userId: mockShift.userId as string,
              shiftId: mockShift.id as string,
            }),
          );
          this.createNewViaDragAndDropService.setQuickShiftDraggable(true);
          this.nzMessageService.error(
            this.translateService.instant('general.something_went_wrong'),
          );

          this.createNewViaDragAndDropService.removeUpdatingId(
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          );
        },
        complete: () => {
          this.createNewViaDragAndDropService.removeUpdatingId(
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          );
        },
      });
    }
  }
}
